<template>
  <EcxSidebar :expanded="expanded">
    <!-- Logo Slot -->
    <template #logo="{ expanded: isExpanded }">
      <RouterLink to="/" class="relative z-10 w-10 h-10 flex items-center justify-center bg-white">
        <img 
          v-if="isExpanded"
          src="/assets/images/ecx_logo.webp" 
          class="max-w-[70px] w-auto h-auto duration-300" 
          alt="ecx_logo"
        />
        <img 
          v-else
          src="/assets/images/small_logo.webp" 
          class="w-auto h-auto max-w-[28px] duration-300" 
          alt="ecx_logo"
        />
      </RouterLink>
    </template>

    <!-- Navigation Items -->
    <template #default="{ expanded: isExpanded }">
      <!-- Início (Dashboard) -->
      <EcxNavItem
        :expanded="isExpanded"
        to="/"
        :label="t('corp.sidebar.home')"
        v-slot="{ isActive }"
      >
        <GridSquaresGradientIcon class="w-4 h-4" :active="isActive" />
      </EcxNavItem>

      <!-- Seção: Cartões -->
      <SidebarSection
        v-if="isExpanded && canSeeCardsSection"
        :title="t('corp.sidebar.sections.cards')"
        :expanded="sectionsExpanded.cards"
        @toggle="toggleSection('cards')"
      >
        <EcxNavItem
          v-if="can('cards', 'index')"
          :expanded="isExpanded"
          to="/cartoes"
          active-on-path-prefix
          :active-path-prefix-exclusions="cardsListPathPrefixExclusions"
          :label="t('corp.sidebar.my_cards')"
          v-slot="{ isActive }"
        >
          <CardsNavGradientIcon class="w-4 h-4" :active="isActive" />
        </EcxNavItem>
        <EcxNavItem
          v-if="can('card_templates', 'index')"
          :expanded="isExpanded"
          to="/cartoes/modelos"
          active-on-path-prefix
          :label="t('corp.sidebar.card_models')"
          v-slot="{ isActive }"
        >
          <CreditCardSingleGradientIcon class="w-4 h-4" :active="isActive" />
        </EcxNavItem>
      </SidebarSection>

      <!-- Seção: Financeiro -->
      <SidebarSection
        v-if="isExpanded && canSeeFinancialSection"
        :title="t('corp.sidebar.sections.financial')"
        :expanded="sectionsExpanded.financial"
        @toggle="toggleSection('financial')"
      >
        <EcxNavItem
          v-if="can('balance', 'show')"
          :expanded="isExpanded"
          to="/conta-digital"
          active-on-path-prefix
          :label="t('corp.sidebar.digital_account')"
          v-slot="{ isActive }"
        >
          <DigitalAccountGradientIcon class="w-4 h-4" :active="isActive" />
        </EcxNavItem>
        <EcxNavItem
          v-if="can('transactions', 'index')"
          :expanded="isExpanded"
          to="/transacoes"
          :label="t('corp.sidebar.transactions')"
          v-slot="{ isActive }"
        >
          <InvoiceGradientIcon class="w-4 h-4" :active="isActive" />
        </EcxNavItem>
      </SidebarSection>

      <!-- Seção: Gestão -->
      <SidebarSection
        v-if="isExpanded && canSeeManagementSection"
        :title="t('corp.sidebar.sections.management')"
        :expanded="sectionsExpanded.management"
        @toggle="toggleSection('management')"
      >
        <EcxNavItem
          v-if="can('users', 'index')"
          :expanded="isExpanded"
          to="/usuarios"
          :label="t('corp.sidebar.users')"
          v-slot="{ isActive }"
        >
          <UserSingleGradientIcon class="w-4 h-4" :active="isActive" />
        </EcxNavItem>

        <EcxNavItem
          v-if="can('teams', 'index')"
          :expanded="isExpanded"
          to="/equipe"
          :label="t('corp.sidebar.team')"
          v-slot="{ isActive }"
        >
          <UsersGroupGradientIcon class="w-4 h-4" :active="isActive" />
        </EcxNavItem>
      </SidebarSection>

      <!-- Seção: Frota -->
      <SidebarSection
        v-if="isExpanded && canSeeFleetSection"
        :title="t('corp.sidebar.sections.fleet')"
        :expanded="sectionsExpanded.fleet"
        @toggle="toggleSection('fleet')"
      >
        <EcxNavItem
          :expanded="isExpanded"
          to="/veiculos"
          active-on-path-prefix
          :label="t('corp.sidebar.vehicles')"
          v-slot="{ isActive }"
        >
          <TruckGradientIcon class="w-4 h-4" :active="isActive" />
        </EcxNavItem>
      </SidebarSection>

      <!-- Quando sidebar está fechada - mostra todos os ícones com separadores e tooltips -->
      <template v-if="!isExpanded">
        <!-- Seção: Cartões -->
        <div v-if="canSeeCardsSection" class="border-b border-grey-100 pb-2 mb-2">
          <EcxNavItem
            v-if="can('cards', 'index')"
            :expanded="false"
            to="/cartoes"
            active-on-path-prefix
            :active-path-prefix-exclusions="cardsListPathPrefixExclusions"
            :label="t('corp.sidebar.my_cards')"
            v-slot="{ isActive }"
          >
            <CardsNavGradientIcon class="w-4 h-4" :active="isActive" />
          </EcxNavItem>
          <EcxNavItem
            v-if="can('card_templates', 'index')"
            :expanded="false"
            to="/cartoes/modelos"
            active-on-path-prefix
            :label="t('corp.sidebar.card_models')"
            v-slot="{ isActive }"
          >
            <CreditCardSingleGradientIcon class="w-4 h-4" :active="isActive" />
          </EcxNavItem>
        </div>

        <!-- Seção: Financeiro -->
        <div v-if="canSeeFinancialSection" class="border-b border-grey-100 pb-2 mb-2">
          <EcxNavItem v-if="can('balance', 'show')" :expanded="false" to="/conta-digital" active-on-path-prefix :label="t('corp.sidebar.digital_account')" v-slot="{ isActive }">
            <DigitalAccountGradientIcon class="w-4 h-4" :active="isActive" />
          </EcxNavItem>
          <EcxNavItem v-if="can('transactions', 'index')" :expanded="false" to="/transacoes" :label="t('corp.sidebar.transactions')" v-slot="{ isActive }">
            <InvoiceGradientIcon class="w-4 h-4" :active="isActive" />
          </EcxNavItem>
        </div>

        <!-- Seção: Gestão -->
        <div v-if="canSeeManagementSection" class="border-b border-grey-100 pb-2 mb-2">
          <EcxNavItem v-if="can('users', 'index')" :expanded="false" to="/usuarios" :label="t('corp.sidebar.users')" v-slot="{ isActive }">
            <UserSingleGradientIcon class="w-4 h-4" :active="isActive" />
          </EcxNavItem>
          <EcxNavItem v-if="can('teams', 'index')" :expanded="false" to="/equipe" :label="t('corp.sidebar.team')" v-slot="{ isActive }">
            <UsersGroupGradientIcon class="w-4 h-4" :active="isActive" />
          </EcxNavItem>
        </div>

        <!-- Seção: Frota -->
        <div v-if="canSeeFleetSection" class="border-b border-grey-100 pb-2 mb-2">
          <EcxNavItem :expanded="false" to="/veiculos" active-on-path-prefix :label="t('corp.sidebar.vehicles')" v-slot="{ isActive }">
            <TruckGradientIcon class="w-4 h-4" :active="isActive" />
          </EcxNavItem>
        </div>

      </template>
    </template>
  </EcxSidebar>
</template>

<script setup lang="ts">
import { reactive, computed } from 'vue'
import { RouterLink } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'

import { useSidebarStore } from '@/stores/sidebar'
import { useManagerCan } from '@/composables/users/useManagerCan'

import { EcxSidebar, EcxNavItem } from '@ecx/ui'
import {
  GridSquaresGradientIcon,
  CreditCardSingleGradientIcon,
  CardsNavGradientIcon,
  DigitalAccountGradientIcon,
  InvoiceGradientIcon,
  TruckGradientIcon,
  UserSingleGradientIcon,
  UsersGroupGradientIcon,
} from '@ecx/ui/icons'

import SidebarSection from './SidebarSection.vue'

const { t } = useI18n()
const sidebarStore = useSidebarStore()
const { can } = useManagerCan()

const { expanded } = storeToRefs(sidebarStore)

// Estado das seções expandidas
const sectionsExpanded = reactive({
  cards: true,
  financial: true,
  management: true,
  fleet: true,
})

const toggleSection = (section: keyof typeof sectionsExpanded) => {
  sectionsExpanded[section] = !sectionsExpanded[section]
}

const canSeeFinancialSection = computed(() => can('balance', 'show') || can('transactions', 'index'));

const canSeeCardsSection = computed(
  () => can('cards', 'index') || can('card_templates', 'index'),
);

const canSeeManagementSection = computed(
  () => can('users', 'index') || can('teams', 'index'),
);

const canSeeFleetSection = computed(() => can('vehicles', 'index'));

/** Rotas irmãs sob `/cartoes` que não devem marcar «Meus cartões» como ativo. */
const cardsListPathPrefixExclusions = ['/cartoes/modelos', '/cartoes/pedidos']
</script>
